import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'

const WOCHENTAGE = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const
const KUERZEL = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

type Wochentag = (typeof WOCHENTAGE)[number]

interface Zelle {
  wochennummer: number
  wochentag: Wochentag
  kursnummer: string | null
  kennung: string | null
}

interface Besetzung {
  fahrerId: number
  fahrerName: string
  turnusposition: number | null
}

interface Turnus {
  id: number
  teamId: number
  teamName: string
  bezeichnung: string
  art: 'SCHULTAGE' | 'FERIEN'
  gueltigAb: string
  wochenzahl: number
  aktiv: boolean
  /** Aus: jede Zeile gehört dauerhaft einem Fahrer, niemand rückt weiter (Stötzel DG 2). */
  rotiert: boolean
  raster: Zelle[]
  besetzung: Besetzung[]
}

interface Team {
  id: number
  name: string
  kuerzel: string
}

/** Schlüssel einer Rasterzelle — Wochenzeile und Wochentag identifizieren sie eindeutig. */
function schluessel(woche: number, tag: Wochentag): string {
  return `${woche}|${tag}`
}

/**
 * Pflege der Turnusse: das Raster, nach dem die Dienstgruppen rotieren.
 *
 * Die Eingabe bildet den Plan des Betriebs nach — Zeilen sind Wochen der Rotation, Spalten
 * die Wochentage, in der Zelle steht die Kursnummer. Leer heißt frei.
 */
export function Turnusse() {
  const [turnusse, setTurnusse] = useState<Turnus[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [gewaehlt, setGewaehlt] = useState<Turnus | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  // Verweise aus den Klärungen öffnen einen bestimmten Turnus: /turnusse?id=17
  const [parameter] = useSearchParams()
  const vorgabeId = Number(parameter.get('id')) || null

  const laden = useCallback(() => {
    setLaedt(true)
    Promise.all([api.get<Turnus[]>('/api/turnus'), api.get<Team[]>('/api/teams')])
      .then(([t, tm]) => {
        setTurnusse(t)
        setTeams(tm)
        if (vorgabeId) {
          setGewaehlt((alt) => alt ?? t.find((x) => x.id === vorgabeId) ?? null)
        }
      })
      .finally(() => setLaedt(false))
  }, [vorgabeId])

  useEffect(() => {
    laden()
  }, [laden])

  function neu() {
    if (teams.length === 0) {
      setFehler('Es ist noch keine Dienstgruppe angelegt. Bitte zuerst unter Teams anlegen.')
      return
    }
    setFehler(null)
    setGewaehlt({
      id: 0,
      teamId: teams[0].id,
      teamName: teams[0].name,
      bezeichnung: `${teams[0].name} Schultage`,
      art: 'SCHULTAGE',
      gueltigAb: new Date().toISOString().slice(0, 10),
      wochenzahl: 9,
      aktiv: true,
      rotiert: true,
      raster: [],
      besetzung: [],
    })
  }

  async function speichern() {
    if (!gewaehlt) return
    setFehler(null)
    setMeldung(null)
    const koerper = {
      teamId: gewaehlt.teamId,
      bezeichnung: gewaehlt.bezeichnung,
      art: gewaehlt.art,
      gueltigAb: gewaehlt.gueltigAb,
      wochenzahl: gewaehlt.wochenzahl,
      rotiert: gewaehlt.rotiert,
      raster: gewaehlt.raster.filter((z) => z.kursnummer),
    }
    try {
      const gespeichert =
        gewaehlt.id === 0
          ? await api.post<Turnus>('/api/turnus', koerper)
          : await api.put<Turnus>(`/api/turnus/${gewaehlt.id}`, koerper)
      setGewaehlt(gespeichert)
      setMeldung('Turnus gespeichert.')
      laden()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.')
    }
  }

  async function positionSetzen(fahrerId: number, wert: string) {
    const turnusposition = wert === '' ? null : Number(wert)
    await api.post('/api/turnus/position', { fahrerId, turnusposition })
    setGewaehlt((alt) =>
      alt
        ? {
            ...alt,
            besetzung: alt.besetzung.map((b) =>
              b.fahrerId === fahrerId ? { ...b, turnusposition } : b,
            ),
          }
        : alt,
    )
  }

  function zelleSetzen(woche: number, tag: Wochentag, wert: string) {
    setGewaehlt((alt) => {
      if (!alt) return alt
      const ohne = alt.raster.filter(
        (z) => !(z.wochennummer === woche && z.wochentag === tag),
      )
      if (!wert.trim()) return { ...alt, raster: ohne }
      // "101 S" in Kursnummer und Lagekennung trennen, wie im Plan des Betriebs notiert.
      const teile = wert.trim().split(/\s+/)
      return {
        ...alt,
        raster: [
          ...ohne,
          {
            wochennummer: woche,
            wochentag: tag,
            kursnummer: teile[0],
            kennung: teile[1] ?? null,
          },
        ],
      }
    })
  }

  const rasterKarte = new Map<string, Zelle>()
  gewaehlt?.raster.forEach((z) => rasterKarte.set(schluessel(z.wochennummer, z.wochentag), z))

  return (
    <div className="karte abschnitt">
      <h2>
        Turnusse
        <span
          style={{
            fontWeight: 400,
            color: 'var(--grau-500)',
            marginLeft: 10,
            fontSize: '0.88rem',
          }}
        >
          die Rotation je Dienstgruppe
        </span>
      </h2>

      {fehler && <div className="fehler">{fehler}</div>}
      {meldung && <div style={{ color: 'var(--gruen-600, green)' }}>{meldung}</div>}

      {laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
            <select
              value={gewaehlt?.id ?? ''}
              onChange={(e) => {
                const id = Number(e.target.value)
                setGewaehlt(turnusse.find((t) => t.id === id) ?? null)
                setMeldung(null)
              }}
            >
              <option value="">— Turnus wählen —</option>
              {turnusse.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.bezeichnung} · ab {t.gueltigAb} ·{' '}
                  {t.rotiert ? `${t.wochenzahl} Wochen` : `${t.wochenzahl} feste Zeilen`}
                </option>
              ))}
            </select>
            <button type="button" onClick={neu}>
              Neuer Turnus
            </button>
          </div>

          {gewaehlt && (
            <>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  marginBottom: 12,
                }}
              >
                <label>
                  Dienstgruppe
                  <br />
                  <select
                    value={gewaehlt.teamId}
                    onChange={(e) =>
                      setGewaehlt({ ...gewaehlt, teamId: Number(e.target.value) })
                    }
                  >
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Bezeichnung
                  <br />
                  <input
                    value={gewaehlt.bezeichnung}
                    onChange={(e) =>
                      setGewaehlt({ ...gewaehlt, bezeichnung: e.target.value })
                    }
                  />
                </label>
                <label>
                  Art
                  <br />
                  <select
                    value={gewaehlt.art}
                    onChange={(e) =>
                      setGewaehlt({
                        ...gewaehlt,
                        art: e.target.value as 'SCHULTAGE' | 'FERIEN',
                      })
                    }
                  >
                    <option value="SCHULTAGE">Schultage</option>
                    <option value="FERIEN">Ferien</option>
                  </select>
                </label>
                <label>
                  gültig ab
                  <br />
                  <input
                    type="date"
                    value={gewaehlt.gueltigAb}
                    onChange={(e) => setGewaehlt({ ...gewaehlt, gueltigAb: e.target.value })}
                  />
                </label>
                <label>
                  Wochen
                  <br />
                  <input
                    type="number"
                    min={1}
                    max={52}
                    style={{ width: 70 }}
                    value={gewaehlt.wochenzahl}
                    onChange={(e) =>
                      setGewaehlt({ ...gewaehlt, wochenzahl: Number(e.target.value) })
                    }
                  />
                </label>
                <label>
                  Rotation
                  <br />
                  <select
                    value={gewaehlt.rotiert ? 'ja' : 'nein'}
                    onChange={(e) =>
                      setGewaehlt({ ...gewaehlt, rotiert: e.target.value === 'ja' })
                    }
                  >
                    <option value="ja">wöchentlich weiterrücken</option>
                    <option value="nein">feste Zeile je Fahrer</option>
                  </select>
                </label>
                <button type="button" onClick={speichern}>
                  Speichern
                </button>
              </div>

              <p style={{ fontSize: '0.85rem', color: 'var(--grau-500)' }}>
                Eine Zelle trägt die Kursnummer, wahlweise mit Lagekennung — etwa
                <code> 101 S</code>. Leer heißt freier Tag.
              </p>

              <div className="tabelle-huelle" style={{ boxShadow: 'none', overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Woche</th>
                      {KUERZEL.map((k) => (
                        <th key={k} style={{ textAlign: 'center' }}>
                          {k}
                        </th>
                      ))}
                      <th style={{ textAlign: 'right' }}>Tage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: gewaehlt.wochenzahl }, (_, i) => i + 1).map(
                      (woche) => {
                        const belegt = WOCHENTAGE.filter(
                          (t) => rasterKarte.get(schluessel(woche, t))?.kursnummer,
                        ).length
                        return (
                          <tr key={woche}>
                            <td>
                              <strong>{woche}.</strong>
                            </td>
                            {WOCHENTAGE.map((tag) => {
                              const zelle = rasterKarte.get(schluessel(woche, tag))
                              const wert = zelle?.kursnummer
                                ? `${zelle.kursnummer}${zelle.kennung ? ' ' + zelle.kennung : ''}`
                                : ''
                              return (
                                <td key={tag} style={{ padding: 2 }}>
                                  <input
                                    value={wert}
                                    onChange={(e) => zelleSetzen(woche, tag, e.target.value)}
                                    style={{ width: 62, textAlign: 'center' }}
                                  />
                                </td>
                              )
                            })}
                            <td style={{ textAlign: 'right' }}>{belegt}</td>
                          </tr>
                        )
                      },
                    )}
                  </tbody>
                </table>
              </div>

              {gewaehlt.id !== 0 && (
                <>
                  <h3 style={{ marginTop: 20 }}>
                    {gewaehlt.rotiert ? 'Einstiegszeilen der Fahrer' : 'Feste Zeile der Fahrer'}
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--grau-500)' }}>
                    {gewaehlt.rotiert
                      ? 'Der Versatz ist die Rotation: Ohne ihn stünden alle Fahrer der Gruppe in derselben Woche.'
                      : 'Ohne Rotation fährt jeder Fahrer Woche für Woche seine eigene Zeile.'}{' '}
                    Leer heißt, der Fahrer nimmt am Turnus nicht teil.
                  </p>
                  <div className="tabelle-huelle" style={{ boxShadow: 'none' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Fahrer</th>
                          <th style={{ width: 140 }}>
                            {gewaehlt.rotiert ? 'Einstiegszeile' : 'Zeile'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {gewaehlt.besetzung.map((b) => (
                          <tr key={b.fahrerId}>
                            <td>{b.fahrerName}</td>
                            <td>
                              <input
                                type="number"
                                min={1}
                                max={gewaehlt.wochenzahl}
                                style={{ width: 70 }}
                                value={b.turnusposition ?? ''}
                                onChange={(e) => positionSetzen(b.fahrerId, e.target.value)}
                              />
                            </td>
                          </tr>
                        ))}
                        {gewaehlt.besetzung.length === 0 && (
                          <tr>
                            <td colSpan={2} style={{ color: 'var(--grau-500)' }}>
                              Dieser Dienstgruppe ist noch kein Fahrer zugeordnet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
